import { Anchor, Breadcrumbs, Text } from '@mantine/core';
import { Link, useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import classes from './Breadcrumb.module.css';
import { ROUTE_LABELS } from '@/constants/routes';
import { useBreadcrumbOverride } from '@/providers/BreadcrumbProvider';
import { shouldIgnoreLastBreadcrumb } from '@/utils/breadcrumb';

interface BreadcrumbItem {
  label: string;
  path: string;
}

const formatSegment = (segment: string): string => {
  return segment
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

interface BreadcrumbProps {
  lastItemOverride?: string;
}

export default function Breadcrumb({ lastItemOverride }: BreadcrumbProps) {
  const location = useLocation();
  const { lastItemOverride: contextOverride } = useBreadcrumbOverride();

  // Use context override if available, otherwise use prop override
  const effectiveOverride = contextOverride || lastItemOverride;

  const breadcrumbs = useMemo((): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);

    // Only show breadcrumbs if there's at least one nested segment (2+ segments)
    if (pathSegments.length < 2) return [];

    const items: BreadcrumbItem[] = [];
    let currentPath = '';

    pathSegments.forEach((segment) => {
      currentPath += `/${segment}`;
      const label = ROUTE_LABELS[segment] || formatSegment(segment);
      items.push({ label, path: currentPath });
    });

    return items;
  }, [location.pathname]);

  // For routes that should ignore segment from URL and use from context
  const shouldIgnoreLast = shouldIgnoreLastBreadcrumb(location.pathname);

  const displayBreadcrumbs =
    shouldIgnoreLast && !effectiveOverride
      ? breadcrumbs.slice(0, -1) // Remove last item
      : breadcrumbs;

  // Don't render breadcrumbs if there are no nested segments
  if (displayBreadcrumbs.length === 0) return null;

  return (
    <Breadcrumbs
      separator="/"
      separatorMargin="xs"
      className={classes.breadcrumbs}
      py={0}
      mt={8}
    >
      {displayBreadcrumbs.map((item, index) => {
        const isLast = index === displayBreadcrumbs.length - 1;
        const displayLabel =
          isLast && effectiveOverride ? effectiveOverride : item.label;

        return isLast ? (
          <Text
            key={item.path}
            size="sm"
            c="brand-black"
            variant="regular14"
            className={classes.activeItem}
            aria-current="page"
          >
            {displayLabel}
          </Text>
        ) : (
          <Anchor
            key={item.path}
            component={Link}
            to={item.path}
            c="brand-primary-1"
            className={classes.link}
          >
            {item.label}
          </Anchor>
        );
      })}
    </Breadcrumbs>
  );
}
